import { gql } from "@apollo/client"

// Re-export report version queries
export {
  CREATE_REPORT_VERSION,
  GET_REPORT_VERSION,
  GET_REPORT_VERSIONS,
  RESTORE_REPORT_VERSION,
} from "./reportVersionQueries"

// History Queries
export const GET_EXAMINATION_HISTORY = gql`
  query GetExaminationHistory(
    $examinationId: ID!
    $page: Int = 0
    $pageSize: Int = 20
    $entryType: String
  ) {
    examinationHistory(
      examinationId: $examinationId
      page: $page
      pageSize: $pageSize
      entryType: $entryType
    ) {
      items {
        id
        examinationId
        entryType
        createdAt
        createdBy {
          id
          displayName
          email
        }
        description
        changeDetails {
          fieldPath
          oldValue
          newValue
          changeType
        }
        metadata
      }
      totalCount
      hasNextPage
      hasPreviousPage
    }
  }
`

// TextBlock Queries
export const GET_TEXT_BLOCKS = gql`
  query GetTextBlocks {
    getTextBlocks {
      id
      name
      block {
        id
        dataType
        editable
        deletable
        isAutoGenerated
        sourceExaminationId
        originalTextblockId
        originalTextBlockDataNodeNo
        tableNumber
        tableCaption
        tableHeader {
          data
          colSpan
        }
        tableBody {
          data
          colSpan
        }
        image
        imageCaption
        imageNumber
        text
      }
      chapter
      isTestRelevant
      norms
      examinations
    }
  }
`

export const UPSERT_TEXT_BLOCK = gql`
  mutation UpsertTextBlock($input: TextBlockInput!) {
    upsertTextBlock(input: $input)
  }
`

export const DELETE_TEXT_BLOCK = gql`
  mutation DeleteTextBlock($ids: [ID!]!) {
    deleteTextBlock(ids: $ids)
  }
`

// Skill Queries
export const GET_SKILLS = gql`
  query GetSkills {
    getSkills {
      id
      name
      person
      testEquipment {
        id
        name
        description
        type
        barcode
      }
    }
  }
`

export const UPSERT_SKILL = gql`
  mutation UpsertSkill($input: SkillInput!) {
    upsertSkill(input: $input)
  }
`

export const DELETE_SKILL = gql`
  mutation DeleteSkill($ids: [ID!]!) {
    deleteSkill(ids: $ids)
  }
`

// Norm Queries
export const GET_NORMS = gql`
  query GetNorms($includeDeleted: Boolean = false) {
    getNorms(includeDeleted: $includeDeleted) {
      id
      name
      branch
      label
      revision
      date
      isHarmonized
      isOutdated
      isDeleted
      deletedAt
      documents {
        id
        filename
        name
        description
        type
      }
      examinationCalculations {
        id
        name
        isAccredited
        location {
          name
          costCenter
          abbreviation
        }
        examinationConfig {
          id
          name
        }
        calculation {
          id
          calculationType
          offerText
          humanPrice {
            name
            price
            cost
            amount
          }
          machinePrice {
            name
            price
            cost
            amount
          }
          connectionPrice {
            name
            price
            cost
            amount
          }
        }
        tags {
          id
          name
        }
      }
    }
  }
`

export const UPSERT_NORM = gql`
  mutation UpsertNorm($input: NormInput!) {
    upsertNorm(input: $input)
  }
`

export const DELETE_NORM = gql`
  mutation DeleteNorm($ids: [ID!]!) {
    deleteNorm(ids: $ids)
  }
`

export const SET_NORMS_DELETED = gql`
  mutation SetNormsDeleted($ids: [ID!]!, $deleted: Boolean!, $deletedAt: String) {
    setNormsDeleted(ids: $ids, deleted: $deleted, deletedAt: $deletedAt)
  }
`

// DeviceDataGroup Queries
export const GET_DEVICE_DATA_GROUPS = gql`
  query GetDeviceDataGroups {
    getDeviceDataGroups {
      id
      name
      isDefault
      isAux
      deviceData {
        id
        name
        dataType
        unit
        isArray
        isCalculation
        isOperatingMode
      }
    }
  }
`

export const UPSERT_DEVICE_DATA_GROUP = gql`
  mutation UpsertDeviceDataGroup($input: DeviceDataGroupInput!) {
    upsertDeviceDataGroup(input: $input) {
      id
      name
      isDefault
      isAux
      deviceData {
        id
        name
        dataType
        unit
        isArray
        isCalculation
        isOperatingMode
      }
    }
  }
`

export const DELETE_DEVICE_DATA_GROUP = gql`
  mutation DeleteDeviceDataGroup($ids: [ID!]!) {
    deleteDeviceDataGroup(ids: $ids)
  }
`

// Task Queries
export const GET_TASKS_CONFIG = gql`
  query getTaskConfigs {
    getTaskConfigs {
      id
      name
      info
      taskSteps
    }
  }
`

export const UPSERT_TASK_CONFIG = gql`
  mutation upsertTaskConfig($input: TaskConfigInput!) {
    upsertTaskConfig(input: $input) {
      id
      name
      info
      taskSteps
    }
  }
`

export const DELETE_TASK_CONFIG = gql`
  mutation deleteTaskConfig($ids: [ID!]!) {
    deleteTaskConfig(ids: $ids)
  }
`

// ExaminationConfig Queries
export const GET_EXAMINATION_CONFIGS = gql`
  query GetExaminationConfigs {
    getExaminationConfigs {
      id
      name
      laboratory {
        id
        name
        manager
      }
      location {
        name
        costCenter
        abbreviation
      }
      skill {
        id
        name
        testEquipment {
          id
          name
          description
          type
          barcode
        }
      }
      eutDataGroups {
        id
        name
        isAux
        isDefault
        deviceData {
          id
          name
          dataType
          unit
          isArray
          isCalculation
          isOperatingMode
        }
      }
      tasks {
        id
        name
        info
        taskSteps
      }

      article
      defaultCalculation {
        id
        calculationType
        offerText
        humanPrice {
          name
          price
          cost
          amount
        }
        machinePrice {
          name
          price
          cost
          amount
        }
        connectionPrice {
          name
          price
          cost
          amount
        }
      }
    }
  }
`

export const UPSERT_EXAMINATION_CONFIG = gql`
  mutation UpsertExaminationConfig($input: ExaminationConfigInput!) {
    upsertExaminationConfig(input: $input)
  }
`

export const DELETE_EXAMINATION_CONFIG = gql`
  mutation DeleteExaminationConfig($ids: [ID!]!) {
    deleteExaminationConfig(ids: $ids)
  }
`

export const SET_EXAM_CONFIGS_OBSOLETE = gql`
  mutation SetExaminationConfigsObsolete($ids: [ID!]!, $obsolete: Boolean!, $obsoleteAt: String) {
    setExaminationConfigsObsolete(ids: $ids, obsolete: $obsolete, obsoleteAt: $obsoleteAt)
  }
`

// Laboratory Queries
export const GET_LABORATORIES = gql`
  query GetLaboratories {
    getLaboratories {
      id
      name
      manager
    }
  }
`

export const UPSERT_LABORATORY = gql`
  mutation UpsertLaboratory($input: LaboratoryInput!) {
    upsertLaboratory(input: $input)
  }
`

export const DELETE_LABORATORY = gql`
  mutation DeleteLaboratory($ids: [ID!]!) {
    deleteLaboratory(ids: $ids)
  }
`

// Person Queries
export const GET_PERSONS = gql`
  query GetPersons {
    getPersons {
      id
      avatarURL
      firstName
      lastName
      email
      phone
      jobTitle
    }
  }
`

export const UPSERT_PERSON = gql`
  mutation UpsertPerson($input: PersonInput!) {
    upsertPerson(input: $input) {
      id
      avatarURL
      firstName
      lastName
      email
      phone
      jobTitle
    }
  }
`

export const DELETE_PERSON = gql`
  mutation DeletePerson($id: ID!) {
    deletePerson(id: $id)
  }
`

// Device Queries
export const GET_DEVICES = gql`
  query GetDevices {
    getDevices {
      id
      name
      description
      type
      barcode
      manufacturer
      model
      serialNumber
      function
      productNumber
      revision
      software
      softwareVersion
      images {
        id
        name
        description
        fileURL
        type
        createdAt
        createdBy
        filename
      }

      ... on DeviceEutGroup {
        productNumber
        revision
        software
        softwareVersion
        assumptions
        devices {
          id
          name
          description
          type
          barcode
          manufacturer
          model
          serialNumber
          function
          productNumber
          revision
          software
          softwareVersion
          note
          images {
            id
            name
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
          deviceDataGroup {
            id
            name
            isDefault
            isAux
            deviceData {
              id
              name
              data {
                value
                note
                unit
              }
              dataType
              unit
              isArray
              isCalculation
              isOperatingMode
            }
          }
          movementData {
            type
            date
            shipmentNumber
            carrier
            location
            note
            eutIsOk
            packageIsOk
          }
        }
        notes {
          id
          note
          createdAt
          createdBy
        }
        movementData {
          type
          date
          shipmentNumber
          carrier
          location
          note
          eutIsOk
          packageIsOk
        }
        deviceDataGroup {
          id
          name
          isDefault
          isAux
          deviceData {
            id
            name
            data {
              value
              note
              unit
            }
            dataType
            unit
            isArray
            isCalculation
            isOperatingMode
          }
        }
      }
      ... on DeviceEut {
        manufacturer
        model
        serialNumber
        function
        productNumber
        revision
        software
        softwareVersion
        note
        movementData {
          type
          date
          shipmentNumber
          carrier
          location
          note
          eutIsOk
          packageIsOk
        }
        images {
          id
          name
          description
          fileURL
          type
          createdAt
          createdBy
          filename
        }
      }
      ... on DeviceTestEquipment {
        manufacturer
        model
        serialNumber
        inventoryNo
        calibrationDate
        calibrationDue
        uncertainty
        teType
        location
        status
        statusCode
      }
    }
  }
`

export const GET_DEVICES_BY_TYPE = gql`
  query GetDevicesByType($type: DeviceType!) {
    getDevicesByType(type: $type) {
      id
      name
      description
      type
      barcode
      manufacturer
      model
      serialNumber
      function
      images {
        id
        name
        description
        fileURL
        type
        createdAt
        createdBy
        filename
      }
      documents {
        id
        name
        description
        fileURL
        type
        createdAt
        createdBy
        filename
      }

      ... on DeviceEutGroup {
        productNumber
        revision
        software
        softwareVersion
        assumptions
        devices {
          id
          name
          description
          type
          barcode
          manufacturer
          model
          serialNumber
          function
          productNumber
          revision
          software
          softwareVersion
          note
          images {
            id
            name
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
          documents {
            id
            name
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
          movementData {
            type
            date
            shipmentNumber
            carrier
            location
            note
            eutIsOk
            packageIsOk
          }
        }
        notes {
          id
          note
          createdAt
          createdBy
        }
        movementData {
          type
          date
          shipmentNumber
          carrier
          location
          note
          eutIsOk
          packageIsOk
        }

        deviceDataGroup {
          id
          name
          isDefault
          isAux
          deviceData {
            id
            name
            data {
              value
              note
              unit
            }
            dataType
            unit
            isArray
            isCalculation
            isOperatingMode
          }
        }
      }
      ... on DeviceEut {
        manufacturer
        model
        serialNumber
        function
        productNumber
        revision
        software
        softwareVersion
        note
        images {
          id
          name
          description
          fileURL
          type
          createdAt
          createdBy
          filename
        }
        documents {
          id
          name
          description
          fileURL
          type
          createdAt
          createdBy
          filename
        }
        movementData {
          type
          date
          shipmentNumber
          carrier
          location
          note
          eutIsOk
          packageIsOk
        }
      }
      ... on DeviceTestEquipment {
        manufacturer
        model
        serialNumber
        inventoryNo
        calibrationDate
        calibrationDue
        uncertainty
        teType
        location
        status
        statusCode
        documents {
          id
          name
          description
          fileURL
          type
          createdAt
          createdBy
          filename
        }
      }
    }
  }
`

export const DELETE_DEVICE = gql`
  mutation DeleteDevice($ids: [ID!]!) {
    deleteDevice(ids: $ids)
  }
`

export const UPSERT_DEVICE_EUT_GROUP = gql`
  mutation UpsertDeviceEutGroup(
    $input: DeviceEutGroupInput!
    $inputEut: [DeviceEutInput!]!
    $isOffer: Boolean!
  ) {
    upsertDeviceEutGroup(input: $input, inputEut: $inputEut, isOffer: $isOffer)
  }
`

export const UPSERT_DEVICE_EUT = gql`
  mutation UpsertDeviceEut($input: DeviceEutInput!) {
    upsertDeviceEut(input: $input)
  }
`

export const UPSERT_DEVICE_AUX = gql`
  mutation UpsertDeviceAux($input: DeviceAuxInput!) {
    upsertDeviceAux(input: $input)
  }
`

export const UPSERT_DEVICE_CONSUMABLE = gql`
  mutation UpsertDeviceConsumable($input: DeviceConsumableInput!) {
    upsertDeviceConsumable(input: $input)
  }
`

export const UPSERT_DEVICE_TEST_EQUIPMENT = gql`
  mutation UpsertDeviceTestEquipment($input: DeviceTestEquipmentInput!) {
    upsertDeviceTestEquipment(input: $input)
  }
`

// Primas Equipment Queries
// Note: Primas equipment minimal query is defined in graphql/queries/primasEquipment.ts

// File Mutations
export const DELETE_FILE = gql`
  mutation DeleteFile($ids: [ID!]!) {
    deleteFile(ids: $ids)
  }
`

export const UPLOAD_FILE = gql`
  mutation UploadFile($input: FileUploadInput!) {
    uploadFile(input: $input)
  }
`

// Project
export const UPSERT_PROJECT = gql`
  mutation UpsertProject($input: ProjectInput!) {
    upsertProject(input: $input)
  }
`

export const DELETE_PROJECT = gql`
  mutation DeleteProjects($ids: [ID!]!) {
    deleteProjects(ids: $ids)
  }
`

export const GET_PROJECTS = gql`
  query GetProjects {
    getProjects {
      id
      name
      description
      specialArrangements
      desiredDate
      plannedDate
      updatedDate
      notes
      contacts {
        contactID
        type
      }
      documents {
        id
        filename
        name
        description
        type
      }
      devices {
        id
        name
        description
        type
        barcode
        manufacturer
        model
        serialNumber
        function
        images {
          id
          name
          description
          fileURL
          type
          createdAt
          createdBy
          filename
        }
        documents {
          id
          name
          description
          fileURL
          type
          createdAt
          createdBy
          filename
        }

        ... on DeviceEutGroup {
          productNumber
          revision
          software
          softwareVersion
          assumptions
          devices {
            id
            name
            description
            type
            barcode
            manufacturer
            model
            serialNumber
            function
            productNumber
            revision
            software
            softwareVersion
            note
            images {
              id
              name
              description
              fileURL
              type
              createdAt
              createdBy
              filename
            }
            documents {
              id
              name
              description
              fileURL
              type
              createdAt
              createdBy
              filename
            }
            movementData {
              type
              date
              shipmentNumber
              carrier
              location
              note
              eutIsOk
              packageIsOk
            }
          }
          notes {
            id
            note
            createdAt
            createdBy
          }
          movementData {
            type
            date
            shipmentNumber
            carrier
            location
            note
            eutIsOk
            packageIsOk
          }
          deviceDataGroup {
            id
            name
            isDefault
            isAux
            deviceData {
              id
              name
              data {
                value
                note
                unit
              }
              dataType
              unit
              isArray
              isCalculation
              isOperatingMode
            }
          }
          examinations {
            id
            name
            isAccredited
            status
            specialArrangements
            operatingModes
            numberOfRuns
            salesQuote
            deviations
            passFailCriteria {
              id
              name
              norm
              examinationConfig
              chapterNeeded
              description {
                id
                dataType
                editable
                deletable
                isAutoGenerated
                sourceExaminationId
                originalTextblockId
                originalTextBlockDataNodeNo
                tableNumber
                tableCaption
                tableHeader {
                  data
                  colSpan
                }
                tableBody {
                  data
                  colSpan
                }
                image
                imageCaption
                imageNumber
                text
              }
              criteriaList {
                id
                name
                operatingModes
                description {
                  id
                  dataType
                  editable
                  deletable
                  isAutoGenerated
                  sourceExaminationId
                  originalTextblockId
                  originalTextBlockDataNodeNo
                  tableNumber
                  tableCaption
                  tableHeader {
                    data
                    colSpan
                  }
                  tableBody {
                    data
                    colSpan
                  }
                  image
                  imageCaption
                  imageNumber
                  text
                }
              }
            }
            tester {
              id
              EmployeeName
              ContactInfo {
                FirstName
                LastName
              }
            }
            startTimestamp
            endTimestamp
            deviceSetup {
              device {
                id
                barcode
                type
                name
              }
              linkedTo {
                device {
                  id
                  barcode
                  type
                  name
                }
                note
              }
              note
              images {
                id
                name
                description
                fileURL
                type
                createdAt
                createdBy
                filename
              }
            }
            history {
              id
              createdAt
              createdBy {
                id
                displayName
                email
                userPrincipalName
              }
              description
            }
            tasks {
              id
              name
              info
              taskSteps
              estimatedTime
              workingTime
              status
              examinator {
                id
                displayName
                email
              }
              testDatas {
                id
                type
                name
                description
                beginTime
                endTime
                ambientConditions
                visualCheck
                createdAt
                createdBy {
                  id
                  displayName
                  email
                }
                witnesses {
                  id
                  contactID
                  title
                  jobTitle
                  firstName
                  lastName
                  displayName
                  email
                  companyName
                  gender
                  phone1
                  phone2
                  phone3
                  active
                  businessAccount
                  languageOrLocale
                }
                image {
                  id
                  name
                  description
                  type
                  createdAt
                  createdBy
                  filename
                }

                deviceTestData {
                  device {
                    id
                    type
                    name
                    barcode
                    description
                  }
                  result {
                    criteriaCatalog
                    criteria
                    operatingMode
                    type
                  }
                  timestamp
                  note
                  images {
                    id
                    name
                    description
                    fileURL
                    type
                  }
                }
              }
            }
            calculation {
              id
              calculationType
              offerText
              humanPrice {
                name
                price
                cost
                amount
              }
              machinePrice {
                name
                price
                cost
                amount
              }
              connectionPrice {
                name
                price
                cost
                amount
              }
            }
            laboratory {
              id
              name
              manager
            }
            location {
              name
              costCenter
              abbreviation
            }

            testParameterBlock {
              id
              name
              block {
                id
                dataType
                editable
                deletable
                isAutoGenerated
                sourceExaminationId
                originalTextblockId
                originalTextBlockDataNodeNo
                tableNumber
                tableCaption
                tableHeader {
                  data
                  colSpan
                }
                tableBody {
                  data
                  colSpan
                }
                image
                imageCaption
                imageNumber
                text
              }
              norms
              examinations
              chapter
              isTestRelevant
            }
            examinationConfig {
              id
              name
              laboratory {
                id
                name
                manager
              }
              location {
                name
                costCenter
                abbreviation
              }
              skill {
                id
                name
                testEquipment {
                  id
                  name
                  description
                  type
                  barcode
                }
              }
              eutDataGroups {
                id
                name
                isAux
                isDefault
                deviceData {
                  id
                  name
                  dataType
                  unit
                  isArray
                  isCalculation
                  isOperatingMode
                }
              }
              tasks {
                id
                name
                info
                taskSteps
              }

              article
              defaultCalculation {
                id
                calculationType
                offerText
                humanPrice {
                  name
                  price
                  cost
                  amount
                }
                machinePrice {
                  name
                  price
                  cost
                  amount
                }
                connectionPrice {
                  name
                  price
                  cost
                  amount
                }
              }
            }
            norm {
              id
              name
              branch
              label
              revision
              date
              isHarmonized
              isOutdated
              documents {
                id
                filename
                name
                description
                type
              }
              examinationCalculations {
                id
                name
                isAccredited
                location {
                  name
                  costCenter
                  abbreviation
                }
                examinationConfig {
                  id
                  name
                }
                calculation {
                  id
                  calculationType
                  offerText
                  humanPrice {
                    name
                    price
                    cost
                    amount
                  }
                  machinePrice {
                    name
                    price
                    cost
                    amount
                  }
                  connectionPrice {
                    name
                    price
                    cost
                    amount
                  }
                }
                tags {
                  id
                  name
                }
              }
            }
            tasks {
              id
              name
              info
              taskSteps
              estimatedTime
              workingTime
              status
              examinator {
                id
                displayName
                email
                userPrincipalName
              }
              testDatas {
                id
                type
                name
                description
                beginTime
                endTime
                ambientConditions
                visualCheck
                createdAt
                createdBy {
                  id
                  displayName
                  email
                }
                image {
                  id
                  name
                  description
                  type
                  createdAt
                  createdBy
                  filename
                }
              }
            }
          }
        }
        ... on DeviceEut {
          id
          note
          name
          productNumber
          revision
          software
          softwareVersion
          images {
            id
            name
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
          documents {
            id
            name
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
          movementData {
            type
            date
            shipmentNumber
            carrier
            location
            note
            eutIsOk
            packageIsOk
          }
        }
        ... on DeviceTestEquipment {
          id
          manufacturer
          inventoryNo
          calibrationDue
          uncertainty
          teType
          documents {
            id
            name
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
        }
        ... on DeviceAux {
          id
          type
          barcode
          name
          description
          manufacturer
          model
          serialNumber
          function
          images {
            id
            name
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
          documents {
            id
            name
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
          movementData {
            type
            date
            shipmentNumber
            carrier
            location
            note
            eutIsOk
            packageIsOk
          }
          deviceDataGroup {
            id
            name
            isDefault
            isAux
            deviceData {
              id
              name
              data {
                value
                note
                unit
              }
              dataType
              unit
              isArray
              isCalculation
              isOperatingMode
            }
          }
        }
      }
      qtp
      lead {
        opportunityID
        businessAccount
        ownerEmployeeName
        status
        stage
        subject
      }
      sales {
        id
        displayName
        email
        userPrincipalName
      }
      management {
        id
        displayName
        email
        userPrincipalName
      }
    }
  }
`

// Examination
export const UPSERT_EXAMINATION = gql`
  mutation UpsertExamination($input: ExaminationInput!) {
    upsertExamination(input: $input)
  }
`

export const DELETE_EXAMINATION = gql`
  mutation DeleteExamination($ids: [ID!]!) {
    deleteExamination(ids: $ids)
  }
`

// Report
export const UPSERT_REPORT = gql`
  mutation UpsertReport($input: ReportInput!) {
    upsertReport(input: $input) {
      reportId
      warnings
      filteredExaminations
    }
  }
`

export const DELETE_REPORT = gql`
  mutation DeleteReport($ids: [ID!]!) {
    deleteReport(ids: $ids)
  }
`

export const COPY_REPORT = gql`
  mutation CopyReport($id: ID!, $newName: String!) {
    copyReport(id: $id, newName: $newName)
  }
`

export const GET_ALL_REPORTS_BASIC_DATA = gql`
  query getAllReportsBasicData {
    getAllReportsBasicData {
      id
      name
      status
      version
      versionNumber
      projectID
      documentNo
      createdAt
      updatedAt
      isLatestVersion
    }
  }
`

export const GET_REPORT_BY_ID = gql`
  fragment ChapterFields on Chapter {
    id # 1.
    examinationId
    type
    name
    chapterNumber
    open
    data {
      id
      dataType
      editable
      deletable
      isAutoGenerated
      sourceExaminationId
      originalTextblockId
      originalTextBlockDataNodeNo
      tableNumber
      tableCaption
      tableHeader {
        data
        colSpan
      }
      tableBody {
        data
        colSpan
      }
      image
      imageCaption
      imageNumber
      text
    }
    subChapter {
      id #1.1
      examinationId
      type
      name
      chapterNumber
      open
      data {
        id
        dataType
        editable
        deletable
        isAutoGenerated
        sourceExaminationId
        originalTextblockId
        originalTextBlockDataNodeNo
        tableNumber
        tableCaption
        tableHeader {
          data
          colSpan
        }
        tableBody {
          data
          colSpan
        }
        image
        imageCaption
        imageNumber
        text
      }
      subChapter {
        id # 1.1.1
        examinationId
        type
        name
        chapterNumber
        open
        data {
          id
          dataType
          editable
          deletable
          isAutoGenerated
          sourceExaminationId
          originalTextblockId
          originalTextBlockDataNodeNo
          tableNumber
          tableCaption
          tableHeader {
            data
            colSpan
          }
          tableBody {
            data
            colSpan
          }
          image
          imageCaption
          imageNumber
          text
        }
        subChapter {
          id # 1.1.1.1
          examinationId
          type
          name
          chapterNumber
          open
          data {
            id
            dataType
            editable
            deletable
            isAutoGenerated
            sourceExaminationId
            originalTextblockId
            originalTextBlockDataNodeNo
            tableNumber
            tableCaption
            tableHeader {
              data
              colSpan
            }
            tableBody {
              data
              colSpan
            }
            image
            imageCaption
            imageNumber
            text
          }
          subChapter {
            id # 1.1.1.1.1
            examinationId
            type
            name
            chapterNumber
            open
            data {
              id
              dataType
              editable
              deletable
              isAutoGenerated
              sourceExaminationId
              originalTextblockId
              originalTextBlockDataNodeNo
              tableNumber
              tableCaption
              tableHeader {
                data
                colSpan
              }
              tableBody {
                data
                colSpan
              }
              image
              imageCaption
              imageNumber
              text
            }
          }
        }
      }
    }
  }

  query GetReportById($id: ID!) {
    getReportById(id: $id) {
      id
      name
      reportURL
      title
      info
      status
      tester {
        id
        firstName
        lastName
        email
      }
      version
      examinations {
        id
        name
        norm {
          id
          name
          label
        }
        examinationConfig {
          id
          name
        }
        isAccredited
        status
        operatingModes
        numberOfRuns
        startTimestamp
        endTimestamp
        deviations
        deviceSetup {
          device {
            id
            name
            description
            type
            barcode
          }
          linkedTo {
            device {
              id
              name
            }
            note
          }
          note
          images {
            id
            name
            description
            description
            fileURL
            type
            createdAt
            createdBy
            filename
          }
        }
        tasks {
          id
          name
          status
          testDatas {
            id
            name
            type
            deviceTestData {
              device {
                id
                name
                type
              }
              result {
                criteriaCatalog
                criteria
                operatingMode
                type
              }
              timestamp
              note
            }
          }
        }
      }
      documentNo
      projectID
      chapter {
        ...ChapterFields
      }
      # New fields for professional report generation
      assignmentNo
      releaseDate
      formNumber
      formRevision
      reviewedBy {
        id
        firstName
        lastName
        email
      }
      reviewerTitle
      testFacility
      classification
      issueStatus
      accreditationStatus
      showDraftWatermark
      createdBy {
        id
        displayName
        email
      }
      revisions {
        id
        revisionNumber
        date
        affectedSections
        reasonsForRevision
        issuedBy {
          id
          firstName
          lastName
          email
        }
      }
      testLocation
      qualificationTestPlan
    }
  }
`

// """// Task Working Time
export const UPDATE_TASK_WORKING_TIME = gql`
  mutation UpdateTaskWorkingTime($taskId: ID!, $examinationId: ID!, $workingTime: Int!) {
    updateTaskWorkingTime(taskId: $taskId, examinationId: $examinationId, workingTime: $workingTime)
  }
`

export const DELETE_UNUSED_EXAMINATIONS = gql`
  mutation DeleteUnusedExaminations {
    deleteUnusedExaminations
  }
`

// Criteria Catalog Queries
export const GET_CRITERIA_CATALOGS = gql`
  query GetCriteriaCatalogs {
    getCriteriaCatalogs {
      id
      name
      norm
      examinationConfig
      chapterNeeded
      description {
        id
        dataType
        editable
        deletable
        isAutoGenerated
        sourceExaminationId
        originalTextblockId
        originalTextBlockDataNodeNo
        tableNumber
        tableCaption
        tableHeader {
          data
          colSpan
        }
        tableBody {
          data
          colSpan
        }
        image
        imageCaption
        imageNumber
        text
      }
      criteriaList {
        id
        name
        operatingModes
        description {
          id
          dataType
          editable
          deletable
          isAutoGenerated
          sourceExaminationId
          originalTextblockId
          originalTextBlockDataNodeNo
          tableNumber
          tableCaption
          tableHeader {
            data
            colSpan
          }
          tableBody {
            data
            colSpan
          }
          image
          imageCaption
          imageNumber
          text
        }
      }
    }
  }
`

export const UPSERT_CRITERIA_CATALOG = gql`
  mutation UpsertCriteriaCatalog($input: CriteriaCatalogInput!) {
    upsertCriteriaCatalog(input: $input)
  }
`

export const DELETE_CRITERIA_CATALOG = gql`
  mutation DeleteCriteriaCatalog($ids: [ID!]!) {
    deleteCriteriaCatalog(ids: $ids)
  }
`
